import { CdkDragDrop, moveItemInArray } from '@angular/cdk/drag-drop';
import {
    AfterViewInit,
    ChangeDetectionStrategy,
    Component,
    EventEmitter,
    HostListener,
    Input,
    OnChanges,
    OnDestroy,
    Optional,
    Output,
    SimpleChanges
} from '@angular/core';
import { TranslateService } from '@ngx-translate/core';
import { GridComponent } from '@progress/kendo-angular-grid';
import { Align } from '@progress/kendo-angular-popup';
import { Subscription } from 'rxjs';
import { applyColumnOrder } from './apply-column-order';

const DRAG_END_CLICK_GRACE_MS = 300;

export interface ConfigurableGridColumn {
    key: string;
    title: string;
    defaultVisible?: boolean;
    /** When provided, the column is only offered in the configurator while this returns true (e.g. hidden by business rules, not by user preference). */
    isAvailable?: () => boolean;
    /**
     * Mirrors a `[locked]="true"` grid column: always visible, always first, never moved. Listed in
     * the popup without checkbox or drag handle.
     */
    locked?: boolean;
}

@Component({
    selector: 'app-column-configurator',
    templateUrl: './column-configurator.component.html',
    styleUrls: ['./column-configurator.component.scss'],
    changeDetection: ChangeDetectionStrategy.Eager,
    standalone: false
})
export class ColumnConfiguratorComponent implements AfterViewInit, OnChanges, OnDestroy {
    @Input() columns: ConfigurableGridColumn[] = [];
    @Input() visibility: Record<string, boolean> = {};
    /** Column keys in the user's preferred order; empty = the order the host declared the columns in. */
    @Input() order: string[] = [];
    @Input() title = 'Configure Grid Columns';
    @Input() resetTitle = 'Restore default grid layout';
    @Input() resetLabel = 'Reset Layout';
    @Input() anchorAlign: Align = { horizontal: 'right', vertical: 'bottom' };
    @Input() popupAlign: Align = { horizontal: 'right', vertical: 'top' };

    @Output() visibilityChange = new EventEmitter<{ columnKey: string; visible: boolean }>();
    @Output() allVisibilityChange = new EventEmitter<boolean>();
    @Output() orderChange = new EventEmitter<string[]>();
    @Output() resetColumns = new EventEmitter<void>();

    isOpen = false;

    private columnsChangesSubscription?: Subscription;
    private lastDragEndedAt = 0;

    /** The configurator is rendered inside the grid's toolbar template, so the grid is its declaration-site ancestor. */
    constructor(
        @Optional() private readonly grid: GridComponent | null,
        private readonly translateService: TranslateService
    ) {}

    get visibleColumns(): ConfigurableGridColumn[] {
        return this.inPreferredOrder(this.columns).filter((column) => this.isAvailable(column));
    }

    /** Rows above this index are locked columns - nothing can be dropped in front of them. */
    get lockedCount(): number {
        return this.visibleColumns.filter((column) => column.locked).length;
    }

    readonly canSortInto = (index: number): boolean => index >= this.lockedCount;

    /**
     * With a locked column in the grid, Kendo needs at least one visible non-locked column, so the
     * last one can't be unchecked.
     */
    isLastVisibleMovable(column: ConfigurableGridColumn): boolean {
        if (column.locked || this.lockedCount === 0 || !this.isColumnVisible(column.key)) {
            return false;
        }
        return this.visibleColumns.filter((c) => !c.locked && this.isColumnVisible(c.key)).length === 1;
    }

    get checkedColumnsCount(): number {
        return this.visibleColumns.filter((column) => this.isColumnVisible(column.key)).length;
    }

    get isDefaultLayout(): boolean {
        return (
            this.order.length === 0 &&
            this.visibleColumns.every((column) => this.isColumnVisible(column.key) === (column.defaultVisible ?? true))
        );
    }

    ngAfterViewInit(): void {
        this.applyOrderToGrid();
        // Columns can come and go after the first render (*ngIf-ed columns, grid re-created behind a loader).
        this.columnsChangesSubscription = this.grid?.columns.changes.subscribe(() => this.applyOrderToGrid());
    }

    ngOnChanges(changes: SimpleChanges): void {
        if (changes['order'] && !changes['order'].firstChange) {
            this.applyOrderToGrid();
        }
    }

    ngOnDestroy(): void {
        this.columnsChangesSubscription?.unsubscribe();
    }

    @HostListener('document:click')
    onDocumentClick(): void {
        // The click that ends a drag can land outside the panel (released a bit off the list); it must
        // not close the popup, so several columns can be reordered in a row.
        if (Date.now() - this.lastDragEndedAt < DRAG_END_CLICK_GRACE_MS) {
            return;
        }
        this.isOpen = false;
    }

    onDragEnded(): void {
        this.lastDragEndedAt = Date.now();
    }

    toggle(event: Event): void {
        event.stopPropagation();
        this.isOpen = !this.isOpen;
    }

    isColumnVisible(columnKey: string): boolean {
        return this.visibility[columnKey] !== false;
    }

    onVisibilityChange(columnKey: string, event: Event): void {
        event.stopPropagation();
        this.visibilityChange.emit({
            columnKey,
            visible: (event.target as HTMLInputElement)?.checked ?? true
        });
    }

    onColumnDrop(event: CdkDragDrop<ConfigurableGridColumn[]>): void {
        const lockedCount = this.lockedCount;
        if (
            event.previousIndex === event.currentIndex ||
            event.previousIndex < lockedCount ||
            event.currentIndex < lockedCount
        ) {
            return;
        }

        const reordered = this.visibleColumns;
        moveItemInArray(reordered, event.previousIndex, event.currentIndex);

        // Columns that aren't offered right now (isAvailable() === false) keep their slots.
        const queue = [...reordered];
        const fullOrder = this.inPreferredOrder(this.columns).map((column) =>
            this.isAvailable(column) ? queue.shift().key : column.key
        );
        this.orderChange.emit(fullOrder);
    }

    setAllVisibility(visible: boolean, event: Event): void {
        event.stopPropagation();
        this.allVisibilityChange.emit(visible);
    }

    resetLayout(event: Event): void {
        event.stopPropagation();
        this.resetColumns.emit();
    }

    private isAvailable(column: ConfigurableGridColumn): boolean {
        return column.isAvailable?.() ?? true;
    }

    /**
     * Locked columns first (the grid always renders them first), then the saved order, then columns
     * without a saved position in declaration order (sort is stable).
     */
    private inPreferredOrder(columns: ConfigurableGridColumn[]): ConfigurableGridColumn[] {
        const locked = columns.filter((column) => column.locked);
        const movable = columns.filter((column) => !column.locked);
        if (this.order.length === 0) {
            return [...locked, ...movable];
        }

        const rank = (column: ConfigurableGridColumn) => {
            const position = this.order.indexOf(column.key);
            return position === -1 ? Number.MAX_SAFE_INTEGER : position;
        };
        return [...locked, ...movable.sort((a, b) => rank(a) - rank(b))];
    }

    private applyOrderToGrid(): void {
        const grid = this.grid;
        if (!grid) {
            return;
        }

        // Deferred: reordering changes the grid's column container, which is already checked in this pass.
        Promise.resolve().then(() =>
            applyColumnOrder(grid, this.columns, this.order, (text) => this.translateService.instant(text))
        );
    }
}
